// src/components/OS/OSCard.jsx
import React from 'react';
import {
  Wrench, Pencil, Eye, Play, Check, FileText, Trash2, RefreshCw, AlertTriangle
} from 'lucide-react';
import {
  tempoDesde as utilsTempoDesde,
  formatarData as utilsFormatarData
} from '../../utils/format';

/* ===== Botão tom-sobre-tom (ícone sem fundo) ===== */
const toneStyles = (tone) => {
  switch (tone) {
    case 'primary':
      return { btn: 'text-blue-800 bg-blue-100 hover:bg-blue-200 dark:text-blue-300 dark:bg-blue-900/50 dark:hover:bg-blue-900', icon: 'text-current' };
    case 'success':
      return { btn: 'text-green-800 bg-green-100 hover:bg-green-200 dark:text-green-300 dark:bg-green-900/50 dark:hover:bg-green-900', icon: 'text-current' };
    case 'warning':
      return { btn: 'text-yellow-800 bg-yellow-100 hover:bg-yellow-200 dark:text-yellow-300 dark:bg-yellow-900/50', icon: 'text-current' };
    case 'danger':
      return { btn: 'text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-300 dark:bg-red-900/50 dark:hover:bg-red-900', icon: 'text-current' };
    default:
      return { btn: 'text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700', icon: 'text-current' };
  }
};

const ActionBtn = ({ tone='neutral', icon: Icon, as='button', className='', disabled, children, ...props }) => {
  const { btn, icon } = toneStyles(tone);
  const base = `inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md ${btn} ${disabled ? 'opacity-60 cursor-not-allowed' : ''} ${className}`;
  const IconEl = Icon ? <Icon size={16} className={icon} /> : null;

  if (as === 'a') return <a className={base} {...props}>{IconEl} {children}</a>;
  return <button className={base} disabled={disabled} {...props}>{IconEl} {children}</button>;
};

const InfoRow = ({ label, children }) => (
  <div className="text-sm text-gray-700 dark:text-gray-200">
    <span className="font-semibold text-gray-800 dark:text-gray-100">{label}</span>{' '}
    <span className="break-words">{children}</span>
  </div>
);

/* ===== Helpers de status ===== */
const normalizeStatus = (status='') => String(status || '').toLowerCase().trim();

const fallbackNfeView = (status = '') => {
  const s = normalizeStatus(status);
  if (!s) return { text: 'Não emitida', badge: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-400' };
  if (s.includes('autoriz')) return { text: 'Autorizada', badge: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' };
  if (s.includes('cancelad')) return { text: 'Cancelada', badge: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-500' };
  if (s.includes('deneg')) return { text: 'Denegada', badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' };
  if (s.includes('erro') || s.includes('rejei') || s.includes('falha')) return { text: 'Rejeitada', badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' };
  if (s.includes('process') || s.includes('aguard') || s.includes('pend')) return { text: 'Aguardando autorização', badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
  return { text: status, badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
};
const fallbackNfseView = (status = '') => {
  const s = normalizeStatus(status);
  if (!s) return { text: 'Não emitida', badge: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-400' };
  if (s.includes('cancelad')) return { text: 'Cancelada', badge: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-500' };
  if (s.includes('autoriz') || s.includes('emitid') || s.includes('aprov')) return { text: 'Emitida', badge: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' };
  if (s.includes('erro') || s.includes('rejei') || s.includes('falha')) return { text: 'Rejeitada', badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' };
  if (s.includes('process') || s.includes('aguard') || s.includes('pend')) return { text: 'Aguardando autorização', badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
  return { text: status, badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' };
};
const asErrorView = (view, hasError) => hasError ? { text: 'Rejeitada', badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' } : view;

const isCancelado = (status) => normalizeStatus(status).includes('cancelad');
const isNfeEmitida  = (status) => {
  const s = normalizeStatus(status);
  return (s.includes('autoriz') || s.includes('emitid') || s.includes('aprov')) && !isCancelado(s);
};
const isNfseEmitida = (status) => {
  const s = normalizeStatus(status);
  return (s.includes('autoriz') || s.includes('emitid') || s.includes('aprov')) && !isCancelado(s);
};

const Chip = ({ text, className = '', dotClass = '' }) => (
  <span className={`inline-flex items-center gap-2 text-xs font-semibold px-2 py-1 rounded border ${className}`}>
    <i className={`w-2 h-2 rounded-full ${dotClass}`} />
    {text}
  </span>
);

const isPendingText = (txt) => ['Aguardando autorização', 'Processando'].includes(txt);

/* ===================== CARD ===================== */
const OSCard = ({
  os,
  statusColors,
  nfseLoading,
  nfeLoading,
  onEditar,
  onIniciarAtendimento,
  onFinalizarOS,
  onAbrirDetalhe,
  onEmitirNfse,
  onEmitirNfe,
  onEmitirNota, // fallback
  onConsultarNfse,
  onConsultarNfe,
  onExcluir,
  onAbrirNotasFiscais,
  tempoDesde = utilsTempoDesde,
  formatarData = utilsFormatarData,
  nfStatusView = fallbackNfeView,
  nfseStatusView = fallbackNfseView,
}) => {
  const temServico = (os.servicos?.length || 0) > 0;
  const temProduto = (os.produtos?.length || 0) > 0;

  const sNfe  = normalizeStatus(os.nfeStatus);
  const sNfse = normalizeStatus(os.nfseStatus);

  const nfeErro  = /erro|rejei|deneg/.test(sNfe)  || !!os.nfeUltimoErro;
  const nfseErro = /erro|rejei|deneg/.test(sNfse) || !!os.nfseUltimoErro;

  const nfeV  = asErrorView(nfStatusView(os.nfeStatus), nfeErro);
  const nfseV = asErrorView(nfseStatusView(os.nfseStatus), nfseErro);

  const nfeJaEmitida  = (os.nfeEmitida  || isNfeEmitida(os.nfeStatus))  && !nfeErro;
  const nfseJaEmitida = (os.nfseEmitida || isNfseEmitida(os.nfseStatus)) && !nfseErro;

  // Pendência (com base no texto já traduzido) + “rejeitada?”
  const pendNfeText  = isPendingText(nfeV.text);
  const pendNfseText = isPendingText(nfseV.text);
  const nfeRejeitada  = nfeV.text === 'Rejeitada';
  const nfseRejeitada = nfseV.text === 'Rejeitada';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md flex flex-col transition hover:shadow-xl hover:-translate-y-0.5 overflow-hidden">
      {/* Cabeçalho */}
      <div className="p-4 flex justify-between items-start">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-md">
            <Wrench className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold px-2 py-1 bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-200 rounded-full">
                OS #{os.numeroOS || '...'}
              </span>
              <h3 className="font-bold text-lg text-gray-800 dark:text-gray-100">{os.clienteNome || '-'}</h3>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Veículo: {os.veiculoDescricao || '-'}</p>
          </div>
        </div>
      </div>

      {/* Corpo */}
      <div className="p-4 space-y-3 border-t border-b dark:border-gray-700">
        <InfoRow label="Status:">
          <span className={`inline-block text-xs font-semibold px-2 py-1 rounded border mr-2 ${statusColors?.[os.status] || ''}`}>
            {os.status}
          </span>
          {os.status !== 'Finalizada' && os.dataAbertura && (
            <span className="text-gray-400 ml-1">(há {tempoDesde(os.dataAbertura)})</span>
          )}
        </InfoRow>

        <InfoRow label="Abertura:">{formatarData(os.dataAbertura)}</InfoRow>

        <InfoRow label="Valor total:">
          <span className="font-semibold text-green-700 dark:text-green-400">
            R$ {(Number(os.valorTotalGeral) || 0).toFixed(2)}
          </span>
        </InfoRow>

        {/* Linhas compactas de status fiscal */}
        <div className="flex items-center gap-2 text-xs">
          <FileText className="text-blue-700 dark:text-blue-300" size={16} />
          <span className="font-semibold">Status NFS-e:</span>
          <Chip text={nfseV.text} className={nfseV.badge} dotClass={nfseV.dot} />
          {isPendingText(nfseV.text) && !nfseErro && (
            <span className="ml-1 text-blue-600 dark:text-blue-300 animate-pulse">Aguardando autorização...</span>
          )}
          {nfseErro && (
            <span className="ml-2 text-red-700 dark:text-red-400 inline-flex items-center gap-1">
              <AlertTriangle size={14} /> Erro
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 text-xs">
          <FileText className="text-green-800 dark:text-green-300" size={16} />
          <span className="font-semibold">Status NF-e:</span>
          <Chip text={nfeV.text} className={nfeV.badge} dotClass={nfeV.dot} />
          {isPendingText(nfeV.text) && !nfeErro && (
            <span className="ml-1 text-green-700 dark:text-green-300 animate-pulse">Aguardando autorização...</span>
          )}
          {nfeErro && (
            <span className="ml-2 text-red-700 dark:text-red-400 inline-flex items-center gap-1">
              <AlertTriangle size={14} /> Erro
            </span>
          )}
        </div>
      </div>

      {/* Rodapé */}
      <div className="bg-gray-50 dark:bg-gray-800/50 border-t dark:border-gray-700 rounded-b-xl">
        <div className="min-h-20 p-3 flex flex-wrap items-center justify-end gap-2">
          {os.status === 'Aberta' && (
            <>
              <ActionBtn tone="neutral" icon={Pencil} onClick={() => onEditar?.(os)}>Editar</ActionBtn>
              <ActionBtn tone="warning" icon={Play} onClick={() => onIniciarAtendimento?.(os)}>Iniciar atendimento</ActionBtn>
            </>
          )}

          {os.status === 'Em andamento' && (
            <ActionBtn tone="success" icon={Check} onClick={() => onFinalizarOS?.(os)}>Finalizar OS</ActionBtn>
          )}

          <ActionBtn tone="primary" icon={Eye} onClick={() => onAbrirDetalhe?.(os)}>Detalhar</ActionBtn>

          {os.status === 'Finalizada' && typeof onAbrirNotasFiscais === 'function' && (
            <ActionBtn tone="primary" icon={FileText} onClick={() => onAbrirNotasFiscais(os)}>Notas Fiscais</ActionBtn>
          )}

          {/* ======== NFS-e ======== */}
          {(onEmitirNfse || onEmitirNota) && os.status === 'Finalizada' && temServico && !nfseJaEmitida && (
            <ActionBtn
              tone="primary"
              icon={nfseLoading?.[os.id] ? RefreshCw : FileText}
              onClick={() => (onEmitirNfse ? onEmitirNfse(os) : onEmitirNota?.(os, 'nfse'))}
              disabled={!!nfseLoading?.[os.id] || (pendNfseText && !nfseRejeitada)}
              title={pendNfseText && !nfseRejeitada ? 'Aguardando autorização da prefeitura' : undefined}
            >
              {nfseRejeitada ? 'Emitir novamente NFS-e' : 'Emitir NFS-e'}
            </ActionBtn>
          )}

          {(onEmitirNfse || onEmitirNota) && os.status === 'Finalizada' && temServico && nfseJaEmitida && (
            <ActionBtn tone="primary" icon={FileText} disabled title="NFS-e já emitida">
              Emitir NFS-e
            </ActionBtn>
          )}

          {typeof onConsultarNfse === 'function' &&
            os.status === 'Finalizada' && temServico && os.nfseRef && !nfseJaEmitida && (
              <ActionBtn tone="primary" icon={RefreshCw} onClick={() => onConsultarNfse(os)} disabled={!!nfseLoading?.[os.id]}>
                Atualizar status da NFS-e
              </ActionBtn>
            )}

          {/* ======== NF-e ======== */}
          {(onEmitirNfe || onEmitirNota) && os.status === 'Finalizada' && temProduto && !nfeJaEmitida && (
            <ActionBtn
              tone="success"
              icon={nfeLoading?.[os.id] ? RefreshCw : FileText}
              onClick={() => (onEmitirNfe ? onEmitirNfe(os) : onEmitirNota?.(os, 'nfe'))}
              disabled={!!nfeLoading?.[os.id] || (pendNfeText && !nfeRejeitada)}
              title={pendNfeText && !nfeRejeitada ? 'Aguardando autorização da SEFAZ' : undefined}
            >
              {nfeRejeitada ? 'Emitir novamente NF-e' : 'Emitir NF-e'}
            </ActionBtn>
          )}

          {(onEmitirNfe || onEmitirNota) && os.status === 'Finalizada' && temProduto && nfeJaEmitida && (
            <ActionBtn tone="success" icon={FileText} disabled title="NF-e já emitida">
              Emitir NF-e
            </ActionBtn>
          )}

          {typeof onConsultarNfe === 'function' &&
            os.status === 'Finalizada' && temProduto && (os.nfeRef || os.nfeChave) && !nfeJaEmitida && (
              <ActionBtn tone="success" icon={RefreshCw} onClick={() => onConsultarNfe(os)} disabled={!!nfeLoading?.[os.id]}>
                Atualizar status da NF-e
              </ActionBtn>
            )}

          {os.status !== 'Finalizada' && (
            <ActionBtn tone="danger" icon={Trash2} onClick={() => onExcluir?.(os)}>Excluir OS</ActionBtn>
          )}
        </div>
      </div>
    </div>
  );
};

export default OSCard;
